"use client";

import React, { ChangeEvent, useEffect, useId, useRef, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import PhoneInputWithCountrySelect, {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import { E164Number } from "libphonenumber-js/core";
import { handleCampaignID, insertDataInNetEnquiries } from "@/lib/utils";
import { QueryParams } from "@/types/forms";
import {
  getQueryParamsFromUrl,
  googleTagManager,
} from "@/components/common/utils";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import { usePathname, useRouter } from "next/navigation";
import {
  WebinarKey,
  WebinarScheduleEntry,
  getWebinarEntry,
  getOrdinalDateParts,
} from "@/data/webinarSchedule";
import { getFormOneDataFromLocal } from "@/lib/edcrypt";
import { MilesAlert } from "@/lib/alerts";
import Link from "next/link";
import useMilesOneDeepLink from "@/hooks/useMilesOneDeepLink";
import { CircleCheck } from "lucide-react";
import {
  useEvent,
  useWebinarSchedule,
  useWebinarScheduleLoading,
} from "@/contexts/EventContext";
import WebinarDateSkeleton from "@/components/common/WebinarDateSkeleton";
import { captureAndStoreQueryParamsBase64 } from "@/lib/queryParams";
import { useWhatsappLink } from "@/hooks/useWhatsappLink";
import dynamic from "next/dynamic";
import type { EligibilityUser } from "@/components/newPageComponent/cpa-new/eligibility/EligibilityCalculatorModal";

const EligibilityCalculatorModal = dynamic(
  () =>
    import("@/components/newPageComponent/cpa-new/eligibility/EligibilityCalculatorModal"),
  { ssr: false },
);

interface Vertical {
  Accouting: "accounting";
  Business: "business";
  Healthcare: "healthcare";
}
const makeCountryCodeDisplay = (darkBackground: boolean) => {
  const Component = ({ country }: { country: string }) => {
    const callingCode = country ? getCountryCallingCode(country as any) : "";
    return (
      <span
        className={`inline-block min-w-[30px] text-sm font-medium ${
          darkBackground ? "text-[#FFF]" : "text-[#595858]"
        }`}
      >
        +{callingCode}
      </span>
    );
  };
  Component.displayName = "CountryCodeDisplay";
  return Component;
};

// Renders "12th Sep, Saturday at" from a WebinarScheduleEntry, matching the
// ordinal-date style used across the static webinar headings below.
const WebinarOrdinalDate: React.FC<{ entry: WebinarScheduleEntry }> = ({
  entry,
}) => {
  const dateParts = getOrdinalDateParts(entry.date);
  return (
    <>
      {dateParts.day}
      <sup>{dateParts.suffix}</sup> {dateParts.month.slice(0, 3)}, {entry.day}{" "}
      at
    </>
  );
};

type EnqueryFormProps = {
  course?: string;
  courseId?: number;
  formHeading?: string;
  formType: string;
  isAStepperForm?: boolean;
  subCategory?: string;
  submitButtonName: string;
  successMessage: SuccessMessageType;
  verticalName?: VerticalCategory;
  darkBackground: boolean;
  pageName?: string;
  defaultCityLocation?: string;
  webinarId?: number;
  isWebinarPage?: boolean;
  isWhatsAppLinkAvailable?: boolean;
  whatsAppMessage?: string;
  gtmSubmitEvent?: string;
  onSuccessStateChange?: (_isSuccess: boolean) => void;
  eventTitle?: string;
  eventDate?: string;
  eventTime?: string;
  // Opens the CPA eligibility calculator popup after a successful submit,
  // in place of the /thank-you redirect on cpa/cma pages.
  showEligibilityCalculator?: boolean;
  // Hides the "Live: <date>" heading while keeping formHeading-driven behaviour.
  hideFormHeading?: boolean;
};

const EnqueryForm: React.FC<EnqueryFormProps> = ({
  course,
  courseId,
  formHeading,
  formType,
  isAStepperForm = false,
  hideFormHeading = false,
  subCategory,
  submitButtonName,
  successMessage,
  verticalName,
  darkBackground = false,
  defaultCityLocation,
  isWebinarPage = false,
  pageName,
  webinarId,
  isWhatsAppLinkAvailable = false,
  whatsAppMessage,
  gtmSubmitEvent = "gtm_form1_filled",
  onSuccessStateChange,
  eventTitle,
  eventDate,
  eventTime,
  showEligibilityCalculator = false,
}) => {
  const waGradientId = useId();
  const initialState = {
    connectWhatsApp: true,
    course: courseId !== null ? courseId : "",
    date: "",
    email: "",
    highest_education: "",
    name: "",
    nationality: "",
    phone: "",
    slot: "",
    vertical: "",
  };

  const btnInitalData = {
    text: submitButtonName ? submitButtonName : "Submit",
    disabled: false,
    show: true,
  };

  const [formState, setFormState] = useState({
    message: "",
    error: "",
  });

  const latestEvent = useEvent(pageName);
  const formattedDate = latestEvent?.formattedDate
    ? latestEvent.formattedDate.split(" ").slice(0, 2).join(" ")
    : "";
  const time = latestEvent?.time ? latestEvent?.time : "4:00 PM";
  const day = latestEvent?.day ? latestEvent?.day : "Saturday";
  const path = usePathname();
  const router = useRouter();
  // /cpa, /cpa/*, /cma, /cma/* get an in-app thank-you page instead of the
  // sitewide "gtm_form1_filled" -> external-redirect flow (see submitFormData).
  const isCpaOrCmaPage =
    path === "/cpa" ||
    path?.startsWith("/cpa/") ||
    path === "/cma" ||
    path?.startsWith("/cma/");
  // path === "/ea";
  const thankYouCourse = courseId === 1 ? "cpa" : courseId === 2 ? "cma" : "ea";
  const thankYouWebinarKey: WebinarKey =
    formHeading === FormHeading.AI_INTEGRATED_WEBINAR_CPA
      ? "monthly_cpa"
      : formHeading === FormHeading.AI_INTEGRATED_WEBINAR_CMA
        ? "monthly_cma"
        : "weekly";
  const webinarSchedule = useWebinarSchedule();
  const isScheduleLoading = useWebinarScheduleLoading();
  const weeklyWebinarEntry = getWebinarEntry("weekly", webinarSchedule);
  const cpaWebinarEntry = getWebinarEntry("monthly_cpa", webinarSchedule);
  const cmaWebinarEntry = getWebinarEntry("monthly_cma", webinarSchedule);
  const formRef = useRef<HTMLFormElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(initialState);
  const [formBtnText, setFormBtnText] = useState(btnInitalData);
  const [tkId, setTkId] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [eligibilityUser, setEligibilityUser] =
    useState<EligibilityUser | null>(null);
  const { waLink, handleWhatsAppClick } = useWhatsappLink({
    skip: !isWhatsAppLinkAvailable,
    buttonLocation: "sticky_footer_form",
    defaultMessage: whatsAppMessage,
  });

  // Memoize the country-code flag component so PhoneInput keeps a stable reference
  const CountryCodeDisplay = React.useMemo(
    () => makeCountryCodeDisplay(darkBackground),
    [darkBackground],
  );

  const { tryOpenApp } = useMilesOneDeepLink();
  const LOCAL_STORE_NAME = "MILES_NEXT_F1_REF_ENQUIRY";

  const validateField = (
    value: any,
    errorMessage: string,
    type: string,
  ): boolean => {
    const validRegex = (email: string) => {
      if (email.endsWith("@mileseducation.com")) {
        return /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
      }
      return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
    };
    let isValid = true;

    switch (type) {
      case "empty":
        isValid = !(value === null || value === undefined || value === "");
        break;
      case "empty_course":
        isValid = !(
          value === null ||
          value === undefined ||
          value === "" ||
          value === 0
        );
        break;
      case "email":
        isValid = value ? validRegex(value) : false;
        break;
      case "phoneLength":
        if (value) {
          isValid = isPossiblePhoneNumber(value) && isValidPhoneNumber(value);
        } else {
          isValid = false;
        }
        break;
    }

    return isValid;
  };

  const getSuccessMessage = (successMessage: SuccessMessageType) => {
    switch (successMessage) {
      case SuccessMessageType.QUICK_CONNECT:
        return (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto mb-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h3
              className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-2xl text-xl font-bold text-center mb-2`}
            >
              Thank you for your ​i​nquiry.
            </h3>
            <h2
              className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-2xl text-xl font-normal text-center mb-4`}
            >
              Our programme advisor will get back to you shortly!
            </h2>
          </>
        );
      case SuccessMessageType.EVENTS:
        return (
          <div className="flex justify-center items-center my-8 min-h-[400px]">
            <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-8 w-[90%] max-w-[600px] text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto mb-6 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
                />
              </svg>
              <h2 className="text-gray-800 font-bold text-2xl md:text-3xl mb-4">
                Registration Successful!
              </h2>
              <p className="text-gray-600 text-base md:text-lg">
                Thank you for registering. We have received your details and
                will get back to you shortly with more information.
              </p>
            </div>
          </div>
        );
      case SuccessMessageType.ACCOUNTING:
        return (
          <>
            <div className="hidden md:block">
              <div className="w-full pb-4 flex justify-center">
                <CircleCheck className="w-10 h-10 text-blue-500" />
              </div>
              <p
                className={`rounded-md md:text-[18px] font-bold text-center ${darkBackground ? "text-white" : "text-[#3399FF]"}`}
              >
                {/* Your online meet is confirmed! */}
                Thank you! Your details have been received.
              </p>
              <div className="text-center">
                <p
                  className={` mt-2 text-base font-semibold ${darkBackground ? "text-white" : "text-[#1D1D1D]"}`}
                >
                  {/* You’re all set. Webinar details have been sent to your Email &
                  WhatsApp. */}
                  One of our academic experts
                  <span style={{ display: "block" }}>
                    will reach out to you shortly.
                  </span>
                </p>
              </div>
              <div className="rounded-[20px] bg-[linear-gradient(180deg,_#0B2A4D_0%,_#26467A_100%)] p-5 mt-4">
                <div className="text-center">
                  <p className="font-bold text-white mt-2 leading-[100%] text-base w-full">
                    Get Miles One App
                  </p>
                  <p className="font-normal text-white mt-2 leading-[100%] text-xs w-full">
                    The #1 App for AI-Ready{" "}
                    {formHeading === FormHeading.TALK_TO_CAIRA_AUDITORS
                      ? " Auditors"
                      : " Accountants"}
                  </p>
                </div>
                <div className="flex justify-center items-center gap-3 mt-4">
                  <div className="flex flex-col gap-2">
                    <Link
                      href="https://apps.apple.com/us/app/miles-us-pathway/id6504799221"
                      target="_blank"
                      className="flex items-center gap-2 px-2 py-2 rounded-[8px] bg-white/10 w-[130px] border-[1px] border-white/10"
                    >
                      <Image
                        src="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp"
                        alt="App Store"
                        width={24}
                        height={24}
                        className="cursor-pointer"
                      />
                      <div className="text-white text-left leading-tight">
                        <p className="text-[10px]">Download on the</p>
                        <p className="text-[16px] font-medium">App Store</p>
                      </div>
                    </Link>

                    <Link
                      href="https://play.google.com/store/apps/details?id=com.miles.one"
                      target="_blank"
                      className="flex items-center gap-2 px-2 py-2 rounded-[8px] bg-white/10 w-[130px] border-[1px] border-white/10"
                    >
                      <Image
                        src="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp"
                        alt="Play Store"
                        width={30}
                        height={30}
                        className="cursor-pointer"
                      />
                      <div className="text-white text-left leading-tight">
                        <p className="text-[10px]">GET IT ON</p>
                        <p className="text-[16px] font-medium">Play Store</p>
                      </div>
                    </Link>
                  </div>
                  <div className="flex flex-col items-center justify-center mt-1 cursor-pointer">
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/home/milesone_qr_code.webp"
                      alt="QR Code"
                      width={95}
                      height={95}
                      className=""
                    />
                    <p className="text-[10px] text-[#EEEEEE]">
                      Scan to download
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:hidden">
              <div className="w-full pb-4 flex justify-center">
                <CircleCheck className="w-10 h-10 text-blue-500" />
              </div>
              <p
                className={`rounded-md font-semibold text-center ${darkBackground ? "text-white" : "text-[#3399FF]"}`}
              >
                {/* Your online meet is confirmed! */}
                Thank you! Your details have been received.
              </p>
              <div className="text-center">
                <p
                  className={`font-semibold mt-2 text-sm ${darkBackground ? "text-white" : "text-[#212953]"}`}
                >
                  {/* You’re all set. Webinar details have been sent to your Email &
                  WhatsApp. */}
                  One of our academic experts
                  <span style={{ display: "block" }}>
                    will reach out to you shortly.
                  </span>
                </p>
              </div>
              <div className="mt-4 bg-[linear-gradient(94deg,_#061A2F_0.32%,_#135295_99.92%)] rounded-xl p-3">
                <div className="flex gap-3 items-center">
                  <Image
                    src="/miles_logo_for_form.png"
                    alt="App Store"
                    width={24}
                    height={14}
                    className="cursor-pointer h-4"
                  />
                  <p className="text-base font-semibold text-white">
                    Download the MilesOne App now!
                  </p>
                </div>
                <button
                  className="w-full px-1 py-2 justify-center rounded-[12px_12px_12px_0px] mt-3 flex bg-white text-[#316BC9] font-semibold"
                  onClick={() => {
                    tryOpenApp();
                  }}
                >
                  Open
                </button>
              </div>
            </div>
          </>
        );
      default:
        return <></>;
    }
  };

  const handlePhoneChange = (value?: E164Number | undefined) => {
    const phoneValue = value ?? "";
    setData((prevData) => ({
      ...prevData,
      phone: phoneValue,
    }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleChange = (
    e: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | Vertical[keyof Vertical]
    >,
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    const newValue =
      type === "checkbox" ? (e.target as HTMLInputElement)?.checked : value;

    setData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const btnLoading = () => {
    setFormBtnText((prev) => ({
      ...prev,
      text: "Submitting...",
      disabled: true,
    }));
  };

  const submitFormData = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Track form submission on Brand page
    if (path === "/miles-us-cpa-miles-us-cma" && formType === "page") {
      googleTagManager("Brand_LP_RequestACallBack", {
        button_location: "banner_form",
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
    }
    if (
      path === "/best-us-cpa-admission-online-course-fees-in-india" &&
      formType === "page"
    ) {
      googleTagManager("CPA_LP_RequestACallBack", {
        button_location: "banner_form",
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
    }
    if (path === "/best-us-cma-coaching-in-india" && formType === "page") {
      googleTagManager("CMA_LP_RequestACallBack", {
        button_location: "banner_form",
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
    }

    try {
      const obj: QueryParams = localStorage?.getItem("seo_params")
        ? JSON.parse(localStorage.getItem("seo_params") ?? "")
        : { gclid: null, tk: null };

      setErrors({});
      const newErrors: { [key: string]: string } = {};

      if (!validateField(data.name, "Please enter your full name", "empty")) {
        newErrors.name = "Please enter your full name";
      }
      if (!validateField(data.phone, "Please enter mobile number", "empty")) {
        newErrors.phone = "Please enter mobile number";
      } else if (
        !validateField(
          data.phone,
          "Please enter valid mobile number",
          "phoneLength",
        )
      ) {
        newErrors.phone = "Please enter valid mobile number";
      }
      if (!validateField(data.email, "Please enter email", "empty")) {
        newErrors.email = "Please enter email";
      }
      if (!validateField(data.email, "Please enter valid email", "email")) {
        newErrors.email = "Please enter valid email";
      }
      const courseProgramId = data.course ? Number(data.course) : null;
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      btnLoading();

      const payload = {
        first_name: data.name,
        phone: data.phone || null,
        email: data.email || null,
        city: defaultCityLocation || "",
        whatsapp_opt_in: Boolean(data.connectWhatsApp),
        campaign_tk: tkId || "e3a268cf-420c-415c-a234-2825d878e688",
        whatsappAllow: 1,
        smsAllow: 1,
        gcl_id: obj?.gclid || null,
        course: data.course || null,
        course_id:
          verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING ||
          verticalName === VerticalCategory.EXECUTIVE_EDUCATION
            ? courseId
            : courseProgramId,
        register_for_webinar: true,
        coming_from: `website-${formType}`,
        page_full_url: window.location.href,
        interested_webinar_id: webinarId || null,
        url_page_path: path,
        user_track_id: localStorage.getItem("track") || null,
        query_params_base64: captureAndStoreQueryParamsBase64() ?? "",
      };

      const { data: resultData, error } =
        await insertDataInNetEnquiries(payload);
      const enquiryBody = resultData?.body;
      const isSuccess = !error && resultData?.statusCode === 201 && enquiryBody;

      if (isSuccess) {
        setData(initialState);
        setFormBtnText(btnInitalData);
        localStorage.setItem(
          "MILES_NEXT_F1_REF_ENQUIRY",
          JSON.stringify({
            ...data,
            savedDate: new Date(),
            id: enquiryBody.id,
            url: window.location.href,
            uid: enquiryBody.uuid,
            miles_uuid: enquiryBody.miles_uuid,
          }),
        );
        sessionStorage.setItem("formSubmitted", "true");
        setFormBtnText((prev) => ({
          ...prev,
          text: submitButtonName,
          disabled: false,
        }));

        const gtmPayload = {
          ...data,
          courseTitle: payload.course,
          course: payload.course_id,
          whatsappAllow: payload.whatsappAllow,
          smsAllow: payload.smsAllow,
          user_track_id: payload.user_track_id,
          coming_from: payload.coming_from,
        };

        if (showEligibilityCalculator) {
          // Same conversion event as the cpa/cma branch, but stay on the page
          // and open the calculator instead of redirecting to /thank-you.
          googleTagManager(
            isCpaOrCmaPage ? "thank_you_page_redirect" : gtmSubmitEvent,
            gtmPayload,
          );
          setFormState({ message: "Success", error: "" });
          setCurrentStep(3);
          setEligibilityUser({
            name: data.name,
            phone: data.phone,
            email: data.email,
          });
        } else if (isCpaOrCmaPage) {
          // Own event name, not gtmSubmitEvent/"gtm_form1_filled" — that one
          // is watched by a GTM tag that redirects off-domain, which would
          // race the in-app push below. Point any GTM tags that should still
          // fire on cpa/cma conversions at "thank_you_page_redirect" instead.
          googleTagManager("thank_you_page_redirect", gtmPayload);
          // source_page_url: the page this form was filled on, which the
          // AI-Ready Check sends to Miles 360 with every record.
          router.push(
            `/thank-you?course=${thankYouCourse}&webinar=${thankYouWebinarKey}&source_page_url=${encodeURIComponent(window.location.href)}`,
          );
          setData(initialState);
        } else {
          setFormState({ message: "Success", error: "" });
          if (!formState.error) {
            if (isAStepperForm) {
              // setCurrentStep(2);
              setCurrentStep(3);
            } else {
              setCurrentStep(3);
            }
          }
          googleTagManager(gtmSubmitEvent, gtmPayload);
        }
      } else {
        const errorMessage = error?.message || "Something went wrong";
        MilesAlert({
          text: errorMessage,
          type: "error",
        }).then(() => {
          setFormBtnText(btnInitalData);
        });
        setFormState({
          message: "Error",
          error: "An unexpected error occurred while adding data",
        });
      }
    } catch (error) {
      if (error) {
        setFormState({
          message: "Error",
          error: "error",
        });
        setFormBtnText(btnInitalData);
      } else {
        setFormState({ message: "", error: "An unexpected error occurred" });
      }
    }
  };

  useEffect(() => {
    getQueryParamsFromUrl();
    (() => {
      const localData = getFormOneDataFromLocal(LOCAL_STORE_NAME);
      if (localData.status) {
        setData((prev) => ({
          ...prev,
          name: localData.data.name,
          phone: localData.data.phone,
          email: localData.data.email,
          whatsappAllow: localData.data.whatsappAllow,
          smsAllow: localData.data.smsAllow,
          intrestedin: localData.data.intrestedin,
          courseTitle: localData.data.courseTitle,
          eduDetails: localData.data.eduDetails,
        }));
        isAStepperForm && setCurrentStep(3);
        setFormBtnText((prev) => ({
          ...prev,
          text: submitButtonName,
        }));
      }
    })();
  }, []);

  useEffect(() => {
    onSuccessStateChange?.(currentStep === 3);
  }, [currentStep, onSuccessStateChange]);

  useEffect(() => {
    setData(initialState);
    setFormBtnText(btnInitalData);
    captureAndStoreQueryParamsBase64();
    const init = async () => {
      try {
        const campaignId = handleCampaignID();
        setTkId(campaignId);
      } catch (error: any) {
        console.error(`Error fetching event data: ${error.message}`);
      }
    };
    init();
  }, []);

  return (
    <div>
      {currentStep === 1 &&
        formState?.message === "" &&
        formState?.error === "" && (
          <div>
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              (subCategory === FinanceAndAccountingSubcategory.CPA ||
                subCategory === FinanceAndAccountingSubcategory.CMA ||
                subCategory === FinanceAndAccountingSubcategory.USP ||
                subCategory === FinanceAndAccountingSubcategory.EA) &&
              formHeading === FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN &&
              !isWebinarPage && (
                <div className="text-center pb-2 border-b border-[#fff] border-opacity-15 mb-4 ">
                  <h3
                    className={`pb-1 ${darkBackground ? "text-white" : "text-[#3399FF]"} text-xl md:text-2xl text-center font-semibold md:leading-[1.2]`}
                  >
                    Want to be an
                    <br />
                    <span className="font-bold">AI-Ready Accountant?</span>
                  </h3>
                  <p
                    className={`font-semibold mx-auto text-xs md:text-base ${darkBackground ? "text-[#fff]/80" : "text-[#000]"}`}
                  >
                    Connect with an Expert
                  </p>
                </div>
              )}
            {formHeading === FormHeading.TALK_TO_CAIRA_ADVISOR && (
              <div className="text-center pb-4 mb-2 w-[330px] mx-auto">
                <h3
                  className={`${darkBackground ? "text-white" : "text-black"} text-[18px] font-bold leading-snug`}
                >
                  Build your{" "}
                  <span className="text-[#FFBA08]">1st AI Agent</span> in 2
                  hours
                </h3>
                <p
                  className={`font-semibold text-sm leading-normal mt-2 ${darkBackground ? "text-white" : " text-gray-600"}`}
                >
                  <span className="text-[#f33]">
                    <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1.5"></span>
                    Live:
                  </span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <WebinarOrdinalDate entry={weeklyWebinarEntry} />{" "}
                      <span className="text-[#FFBA08]">
                        {weeklyWebinarEntry.time}
                      </span>
                    </>
                  )}
                </p>
              </div>
            )}
            {formHeading === FormHeading.TALK_TO_CAIRA_AUDITORS && (
              <div className="text-center pb-4 mb-2 w-[330px] mx-auto">
                <h3
                  className={`${darkBackground ? "text-white" : "text-black"} text-lg font-semibold`}
                >
                  {latestEvent?.title}
                </h3>
                <p
                  className={`text-sm mt-1 ${darkBackground ? "text-[#B1B1B1]" : "text-gray-600"}`}
                >
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{
                          opacity: formattedDate && day ? 1 : 0,
                          y: formattedDate && day ? 0 : 10,
                        }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        On {formattedDate}, {day} at
                      </motion.span>{" "}
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                          opacity: time ? 1 : 0,
                          scale: time ? 1 : 0.9,
                        }}
                        transition={{
                          duration: 0.1,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                      >
                        {time}
                      </motion.span>
                    </>
                  )}
                </p>
              </div>
            )}
            {formHeading === FormHeading.QUICK_CONNECT && (
              <h2
                className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-xl text-center font-semibold pb-4`}
              >
                Connect With Us
              </h2>
            )}
            {formHeading === FormHeading.TALK_TO_AN_EXPERT && (
              <h3 className="text-2xl font-bold text-center leading-normal mb-4">
                {/* Every existing caller of this heading is on a light card, so
                    the dark pair is additive — nothing already shipped changes. */}
                <span className={darkBackground ? "text-white" : "text-[#333]"}>
                  Talk to an{" "}
                </span>
                <span
                  className={darkBackground ? "text-[#FFBA08]" : "text-[#39F]"}
                >
                  Expert
                </span>
              </h3>
            )}
            {formHeading === FormHeading.SCHEDULE_A_VISIT_TO_MILES_OFFICE && (
              <h3 className="text-white text-center text-base font-semibold leading-[25px] px-8 pb-4 lg:text-xl lg:px-2">
                Schedule a visit to{" "}
                <span className="text-[#FFBA08]">Miles Office</span> or meet us
                online
              </h3>
            )}
            {formHeading === FormHeading.LIVE_WEBINAR_CPA_CMA && (
              <h3
                className={`${darkBackground ? "text-white" : "text-black"} text-center text-base font-semibold leading-[25px] px-2 pb-4 lg:text-xl`}
              >
                Live Webinar:{" "}
                <span className="text-[#FFBA08]">AI in Accounting 101</span>
              </h3>
            )}
            {formHeading === FormHeading.LIVE_AI_IN_ACCOUNTING_101_WEBINAR && (
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold leading-normal w-[330px] mx-auto">
                  <span className="text-[#1D1D1D]">{latestEvent?.title}</span>
                </h3>
                <p className="text-[#333] text-sm font-semibold leading-normal mt-1">
                  <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1" />
                  <span className="text-[#f33]">Live:</span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{
                          opacity: formattedDate && day ? 1 : 0,
                          y: formattedDate && day ? 0 : 10,
                        }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        On {formattedDate}, {day} at
                      </motion.span>{" "}
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                          opacity: time ? 1 : 0,
                          scale: time ? 1 : 0.9,
                        }}
                        transition={{
                          duration: 0.1,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                      >
                        {time}
                      </motion.span>
                    </>
                  )}
                </p>
              </div>
            )}
            {formHeading === FormHeading.WEEKLY_WEBINAR && (
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold leading-normal w-[330px] mx-auto">
                  <span
                    className={`${darkBackground ? "text-white" : "text-[#1D1D1D]"}`}
                  >
                    {latestEvent?.title}
                  </span>
                </h3>
                <p
                  className={`text-sm font-semibold leading-normal mt-1 ${darkBackground ? "text-[#fff]" : "text-[#333]"}`}
                >
                  <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1" />
                  <span className="text-[#f33]">Live:</span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <span>
                        On {formattedDate}, {day} at
                      </span>{" "}
                      <span className="text-[#39F]">{time}</span>
                    </>
                  )}
                </p>
              </div>
            )}
            {formHeading === FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN &&
              isWebinarPage && (
                <>
                  <h3
                    className={`${darkBackground ? "text-white" : "text-black"} md:text-base text-center font-semibold pb-4 md:leading-[22px]`}
                  >
                    Live Webinar: AI-Ready {course}
                    <br />
                    By Varun Jain
                    <br />
                    {isScheduleLoading ? (
                      <WebinarDateSkeleton width={150} />
                    ) : (
                      <>
                        <motion.span
                          initial={{ opacity: 0, y: 10 }}
                          animate={{
                            opacity: formattedDate && day ? 1 : 0,
                            y: formattedDate && day ? 0 : 10,
                          }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="text-[#3399FF] text-sm"
                        >
                          On {formattedDate}, {day} at
                        </motion.span>{" "}
                        <motion.span
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{
                            opacity: time ? 1 : 0,
                            scale: time ? 1 : 0.9,
                          }}
                          transition={{
                            duration: 0.1,
                            delay: 0.8,
                            ease: "easeOut",
                          }}
                          className="text-[#3399FF] text-sm font-semibold"
                        >
                          {time}
                        </motion.span>
                      </>
                    )}
                  </h3>
                </>
              )}
            {!hideFormHeading &&
              formHeading === FormHeading.AI_INTEGRATED_WEBINAR_CPA && (
                <>
                  <p className="text-[#FFF] text-base font-semibold text-center pb-4">
                    <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1" />
                    <span className="text-[#f33]">Live:</span>{" "}
                    {isScheduleLoading ? (
                      <WebinarDateSkeleton width={150} />
                    ) : (
                      <>
                        <WebinarOrdinalDate entry={cpaWebinarEntry} />
                        <span className="text-[#FFBA00]">
                          {" "}
                          {cpaWebinarEntry.time}
                        </span>
                      </>
                    )}
                  </p>
                </>
              )}

            {!hideFormHeading &&
              formHeading === FormHeading.AI_INTEGRATED_WEBINAR_CMA && (
                <>
                  <p className="text-[#FFF] text-base font-semibold text-center pb-4">
                    <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1" />
                    <span className="text-[#f33]">Live:</span>{" "}
                    {isScheduleLoading ? (
                      <WebinarDateSkeleton width={150} />
                    ) : (
                      <>
                        <WebinarOrdinalDate entry={cmaWebinarEntry} />
                        <span className="text-[#FFBA00]">
                          {" "}
                          {cmaWebinarEntry.time}
                        </span>
                      </>
                    )}
                  </p>
                </>
              )}

            {formHeading === FormHeading.AI_ACCOUNTING_101_WEBINAR && (
              <div className="text-center mb-3">
                <p
                  className={`text-sm font-semibold leading-normal mt-0 mb-2 md:text-base ${darkBackground ? "text-[#fff]" : "text-[#333]"}`}
                >
                  <span className="inline-block w-2 h-2 bg-[#F33] rounded-full mr-1" />
                  <span className="text-[#F33]">Live:</span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <span>
                        On <WebinarOrdinalDate entry={weeklyWebinarEntry} />
                      </span>{" "}
                      <span className="text-[#FFBA00]">
                        {weeklyWebinarEntry.time}
                      </span>
                    </>
                  )}
                </p>
                {isWhatsAppLinkAvailable && (
                  <div className="flex flex-col gap-3">
                    <a
                      id="whatsapp-chat-widget"
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Chat with us on WhatsApp"
                      className="flex items-center justify-center gap-3 w-full py-1 bg-[#555] rounded-[10px] hover:bg-[#494848] transition-colors no-underline"
                      onClick={handleWhatsAppClick}
                    >
                      {/* Vector redraw of WhatsApp_icon.webp. That file is an
                          80px palette PNG with only 18 alpha steps, so its
                          edges stair-step at any size; the SVG is rasterised
                          per device pixel ratio instead. */}
                      <svg
                        viewBox="-2 -2 28 28"
                        width={24}
                        height={24}
                        className="shrink-0"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <defs>
                          <linearGradient
                            id={waGradientId}
                            x1="0"
                            y1="1"
                            x2="0"
                            y2="0"
                          >
                            <stop offset="0" stopColor="#20B038" />
                            <stop offset="1" stopColor="#60D66A" />
                          </linearGradient>
                        </defs>
                        <path
                          fill="#fff"
                          d="M20.464 3.488A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
                        />
                        <path
                          fill={`url(#${waGradientId})`}
                          d="M12.051 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884Z"
                        />
                        <path
                          fill="#fff"
                          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z"
                        />
                      </svg>
                      <span className="flex flex-col">
                        <span className="text-[10px] font-normal text-white leading-tight tracking-wide">
                          Chat with us on
                        </span>
                        <span className="text-[16px] font-semibold text-white leading-tight text-left">
                          WhatsApp
                        </span>
                      </span>
                    </a>
                    <hr className="border-none h-[2px] bg-[#999]" />
                  </div>
                )}
              </div>
            )}
            {formHeading === FormHeading.ONLINE_EVENT_WEBINAR && (
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold leading-normal w-[330px] mx-auto">
                  <span
                    className={`${darkBackground ? "text-white" : "text-[#1D1D1D]"}`}
                  >
                    {eventTitle}
                  </span>
                </h3>
                {(eventDate || eventTime) && (
                  <p
                    className={`text-sm font-semibold leading-normal mt-1 ${darkBackground ? "text-[#fff]" : "text-[#333]"}`}
                  >
                    <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1" />
                    <span className="text-[#f33]">Live:</span>{" "}
                    {eventDate && <span>On {eventDate}</span>}{" "}
                    {eventTime && (
                      <span className="text-[#39F]">{eventTime}</span>
                    )}
                  </p>
                )}
              </div>
            )}
            {formHeading === FormHeading.MONTHLY_WEBINAR && (
              <div className="text-center mb-4">
                <h3 className="text-[18px] font-bold leading-snug">
                  <span
                    className={darkBackground ? "text-white" : "text-[#1D1D1D]"}
                  >
                    Build your{" "}
                  </span>
                  <span className="text-[#FFBA08]">1st AI Agent</span>
                  <span
                    className={darkBackground ? "text-white" : "text-[#1D1D1D]"}
                  >
                    {" "}
                    in 2 hours
                  </span>
                </h3>
                <p
                  className={`text-sm font-semibold leading-normal mt-2 ${darkBackground ? "text-[#fff]" : "text-[#333]"}`}
                >
                  <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1.5" />
                  <span className="text-[#f33]">Live:</span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <WebinarOrdinalDate entry={weeklyWebinarEntry} />{" "}
                      <span className="text-[#FFBA08]">
                        {weeklyWebinarEntry.time}
                      </span>
                    </>
                  )}
                </p>
              </div>
            )}
            {formHeading === FormHeading.POP_UP_FORM && (
              <>
                <p className="text-left font-semibold text-sm md:text-base text-[#333] mb-5">
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <WebinarOrdinalDate entry={weeklyWebinarEntry} />{" "}
                      <span>{weeklyWebinarEntry.time}</span>
                    </>
                  )}
                </p>
              </>
            )}

            <form ref={formRef} onSubmit={submitFormData}>
              <div className="mb-2">
                <label htmlFor="form-fullname-field" className="sr-only">
                  Full Name
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  id="form-fullname-field"
                  name="name"
                  value={data.name}
                  placeholder="Full Name*"
                  onChange={handleChange}
                  className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.name ? "border-red-500" : darkBackground ? "text-[#FFF] bg-[#000] border-[#333] placeholder:text-[#777]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-[1px] ml-1">
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="mb-2">
                <label htmlFor="phone" className="sr-only">
                  Phone Number
                </label>
                <PhoneInputWithCountrySelect
                  id="phone"
                  name="phone"
                  type="tel"
                  value={data.phone}
                  onChange={(value) =>
                    handlePhoneChange(value as E164Number | undefined)
                  }
                  placeholder="Phone Number*"
                  autoComplete="off"
                  defaultCountry="IN"
                  flagComponent={CountryCodeDisplay}
                  className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none [--PhoneInputCountryFlag-borderColor:transparent] [--PhoneInputCountryFlag-backgroundColor--loading:transparent] [--PhoneInputCountrySelectArrow-opacity:1] [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:font-medium [&_.PhoneInputInput]:text-inherit [&_.PhoneInputInput::placeholder]:opacity-100 [&_.PhoneInputCountry]:bg-transparent [&_.PhoneInputCountry]:text-inherit [&_.PhoneInputCountrySelect]:bg-transparent [&_.PhoneInputCountrySelect]:text-inherit [&_.PhoneInputCountryIcon]:!bg-transparent [&_.PhoneInputCountryIcon]:text-inherit [&_.PhoneInputCountryIcon]:!shadow-none [&_.PhoneInputCountrySelectArrow]:!opacity-100 ${
                    errors.phone
                      ? "border-red-500 bg-white"
                      : darkBackground
                        ? "text-[#FFF] bg-[#000] border-[#333] placeholder:text-[#777] [&_.PhoneInputInput::placeholder]:!text-[#777]"
                        : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858] [&_.PhoneInputInput::placeholder]:!text-[#595858]"
                  }`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-[1px] ml-1">
                    {errors.phone}
                  </p>
                )}
              </div>

              <div className="mb-2">
                <label htmlFor="form-email-field" className="sr-only">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="off"
                  id="form-email-field"
                  value={data.email}
                  placeholder="E-mail*"
                  onChange={handleChange}
                  className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.email ? "border-red-500" : darkBackground ? "text-[#FFF] bg-[#000] border-[#333] placeholder:text-[#777]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-[1px] ml-1">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="mb-2 ml-1 flex items-center justify-start">
                <input
                  id="page"
                  name="connectWhatsApp"
                  type="checkbox"
                  checked={data.connectWhatsApp}
                  onChange={handleChange}
                  className={`relative w-5 h-5 md:min-w-[20px] appearance-none cursor-pointer
                  rounded-full mr-2 border-2 ${darkBackground ? "border-white" : "border-black"}
                  checked:bg-[#39f] checked:border-0
                  after:content-['✓'] after:absolute after:top-1/2 after:left-1/2
                  after:-translate-x-1/2 after:-translate-y-1/2 after:text-[10px]
                  after:font-bold after:text-white after:opacity-0
                  checked:after:opacity-100 after:transition-opacity after:duration-200`}
                />

                <label
                  htmlFor="page"
                  className={` text-[8px] md:text-[9px] font-medium tracking-[-0.373px] cursor-pointer ${
                    darkBackground ? "text-[#D5D5D5]" : "text-[#002551]"
                  }`}
                >
                  I agree to receive updates via
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/whatsapp_icon.webp"
                    alt="Whatsapp logo"
                    width={12}
                    height={12}
                    sizes="12px"
                    loading="lazy"
                    className="mx-[2px] inline-block"
                  />
                  WhatsApp, Email & SMS.
                  <a
                    href="/privacy"
                    target="_blank"
                    className={`ml-1 underline ${
                      darkBackground ? "text-[#D5D5D5]" : "text-[#39F]"
                    }`}
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              <div
                className={`${formHeading === FormHeading.POP_UP_FORM ? "mt-10" : ""}`}
              >
                <button
                  className="font-bold bg-[#3399FF] text-white text-xs md:text-[15px] py-2 w-full capitalize rounded-[20px_20px_20px_0px]"
                  type="submit"
                  disabled={formBtnText.disabled}
                >
                  {formBtnText.text}
                </button>
              </div>
            </form>
          </div>
        )}

      {currentStep === 3 && getSuccessMessage(successMessage)}

      {showEligibilityCalculator && eligibilityUser && (
        <EligibilityCalculatorModal
          user={eligibilityUser}
          onClose={() => setEligibilityUser(null)}
        />
      )}
    </div>
  );
};

export default EnqueryForm;
